/**
 * Demo persona overlay.
 *
 * "Sign in as someone else" used to authenticate as a separate seeded
 * MG impersonation user (Ronnie / Steph / KJ). Those users wrote into
 * the global `users` collection with the real MG employees' email
 * addresses (`ronnie@puntt.ai`, etc.), which collided with the
 * `users.email_1` unique index the moment a real account showed up
 * with the same address — and broke `Reset Demo` with E11000.
 *
 * Replacement: a *display-only* overlay. The current authenticated user
 * keeps their real identity (JWT, userID, permissions all unchanged),
 * but the rendered name and avatar on `state.auth.value` are replaced
 * with the persona's. Every component that reads `auth.value.name` or
 * `auth.value.avatar` (sidebar account chip, comment attribution badges,
 * etc.) automatically picks up the override because the slice is the
 * only source of truth for those two fields.
 *
 * Persistence: localStorage. Survives page reloads (the auth slice's
 * `derive` action re-applies the persona after decoding the JWT) but is
 * scoped to the browser, so different demo machines keep their own
 * choice.
 *
 * Lifecycle: cleared on `Reset Demo`, on logout (the auth slice's
 * `logout` action removes localStorage tokens; we mirror that here),
 * and explicitly via `clearDemoPersona()` from a "Stop impersonating"
 * button on the impersonation card.
 *
 * Scope: nothing about the overlay touches the backend, so no risk of
 * the persona leaking into ticket comments, project ownership, or any
 * server-side write. A keen-eyed prospect would still see the real
 * email in the account dropdown — that's intentional, the overlay only
 * masks what's safe to mask.
 */
const DEMO_PERSONA_KEY = "puntt:demoPersona";

export interface DemoPersona {
  name: string;
  avatar?: string;
  title?: string;
}

/**
 * Read the active persona from localStorage, or `null` if no overlay is
 * applied. Resilient: returns `null` on JSON parse errors rather than
 * throwing, so a corrupted entry never blocks app boot.
 */
export function getStoredDemoPersona(): DemoPersona | null {
  try {
    const raw = localStorage.getItem(DEMO_PERSONA_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DemoPersona;
    if (typeof parsed?.name !== "string" || parsed.name.length === 0) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/** Persist a persona to localStorage. Pass `null` to clear. */
export function setStoredDemoPersona(persona: DemoPersona | null): void {
  if (persona === null) {
    localStorage.removeItem(DEMO_PERSONA_KEY);
    return;
  }
  localStorage.setItem(DEMO_PERSONA_KEY, JSON.stringify(persona));
}
